import { z } from "zod";
import { isValidPhoneNumber } from "react-phone-number-input";

// Shipment-related schemas
export const contactInfoSchema = z.object({
  name: z.string().nonempty("Name is required"),
  company: z.string().optional(),
  phone: z
    .string()
    .refine(isValidPhoneNumber, { message: "Invalid phone number" }),
  email: z
    .union([
      z.string().email("Invalid email address"),
      z.string().length(0),
      z.null(),
    ])
    .optional()
    .nullable(),
});

export const shipmentPackageSchema = z.object({
  weight: z.coerce.number().min(0.01, "Weight must be greater than 0"),
  weightUnit: z.enum(["g", "kg", "lb", "oz"]).default("lb"),
  length: z.coerce.number().min(0.01, "Length must be greater than 0"),
  width: z.coerce.number().min(0.01, "Width must be greater than 0"),
  height: z.coerce.number().min(0.01, "Height must be greater than 0"),
  dimensionUnit: z.enum(["cm", "m", "in", "ft"]).default("in"),
  declaredValueCents: z.coerce.number().optional(),
  isFragile: z.boolean().default(false),
  requiresCooling: z.boolean().default(false),
  description: z.string().optional(),
});

export const addressSchema = z.object({
  line1: z.string().nonempty("Address is required"),
  line2: z.string().optional(),
  city: z.string().nonempty("City is required"),
  province: z.string().nonempty("Province is required"),
  postalCode: z.string().nonempty("Postal code is required"),
  country: z.string().nonempty("Country is required"),
  location: z.object({
    lat: z.number(),
    lng: z.number(),
  }),
});

export const createShipmentSchema = z.object({
  orderId: z.string().optional(), // Optional for new orders
  provider: z.enum(["LOCAL", "SHIPSTATION", "CHITCHATS"]).default("LOCAL"),
  sender: contactInfoSchema,
  recipient: contactInfoSchema,
  pickupAddress: addressSchema,
  deliveryAddress: addressSchema,
  returnAddress: addressSchema.optional(),
  packages: z
    .array(shipmentPackageSchema)
    .min(1, "At least one package is required"),
  pickupNotes: z.string().optional(),
  deliveryNotes: z.string().optional(),
  metadata: z.record(z.any()).optional(),
});

export type CreateOrderSchema = z.infer<typeof createOrderSchema>;
export const createOrderSchema = z.object({
  placerCustomerId: z.string().optional().nullable(),
  pricingTierId: z.string().optional(),
  metadata: z.record(z.any()).optional(),
  shipments: z
    .array(createShipmentSchema)
    .min(1, "At least one shipment is required"),
});

// Update shipment schema for existing orders
export const updateShipmentSchema = z.object({
  status: z
    .enum([
      "DRAFT",
      "RATED",
      "LABELED",
      "SHIPPED",
      "IN_TRANSIT",
      "DELIVERED",
      "EXCEPTION",
      "RETURNED",
      "CANCELLED",
    ])
    .optional(),
  sender: contactInfoSchema.optional(),
  recipient: contactInfoSchema.optional(),
  pickupAddress: addressSchema.optional(),
  deliveryAddress: addressSchema.optional(),
  packages: z.array(shipmentPackageSchema).optional(),
  metadata: z.record(z.any()).optional(),
});

export type UpdateShipmentSchema = z.infer<typeof updateShipmentSchema>;

export type CreateInvoiceSchema = z.infer<typeof createInvoiceSchema>;
export const createInvoiceSchema = z.object({
  customerId: z.string().nonempty("Customer is required"),
  orderIds: z.array(z.string()).min(1, "At least one order must be selected"),
  dateRange: z.object({
    from: z.date(),
    to: z.date(),
  }),
  dueDate: z.date(),
  discount: z
    .object({
      type: z.enum(["percentage", "amount"]),
      value: z.number().min(0).max(100000), // Max $1000 for amount or 100% for percentage
    })
    .optional(),
});

export type CreateOperatorSchema = z.infer<typeof createOperatorSchema>;
export const createOperatorSchema = z.object({
  firstName: z
    .string()
    .min(2, "First name must be at least 2 characters")
    .nonempty("First name is required"),
  lastName: z
    .string()
    .min(2, "Last name must be at least 2 characters")
    .nonempty("Last name is required"),
  email: z
    .string()
    .nonempty("Email is required")
    .email("Invalid email address"),
  phone: z
    .string()
    .refine(isValidPhoneNumber, { message: "Invalid phone number" }),
  dateOfBirth: z
    .string()
    .nonempty("Date of birth is required")
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format (YYYY-MM-DD)"),
  documents: z.object({
    license: z.string().default("NOT_PROVIDED"),
    insurance: z.string().default("NOT_PROVIDED"),
  }),
  workSchedule: z
    .array(
      z.object({
        day: z
          .number()
          .min(1, "Day must be between 1 and 7")
          .max(7, "Day must be between 1 and 7"),
        start: z.string().nonempty("Start time is required"),
        end: z.string().nonempty("End time is required"),
      })
    )
    .min(1, "At least one work schedule is required"),
  dispatchCenterId: z.string().nonempty("Dispatch center is required"),
  authMethod: z.enum(["sms", "email"]).default("sms"),
  address: z.object({
    line1: z.string().nonempty("Address line 1 is required"),
    line2: z.string().optional(),
    city: z.string().nonempty("City is required"),
    province: z.string().nonempty("Province is required"),
    country: z.string().nonempty("Country is required"),
    postalCode: z.string().optional(),
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
  }),
});
